import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const AlgorithmVisualizer = () => {
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('bubble-sort');
  const [array, setArray] = useState([64, 34, 25, 12, 22, 11, 90]);
  const [inputValue, setInputValue] = useState('64,34,25,12,22,11,90');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [speed, setSpeed] = useState(500);

  const algorithms = [
    { value: 'bubble-sort', label: 'Bubble Sort' },
    { value: 'selection-sort', label: 'Selection Sort' },
    { value: 'insertion-sort', label: 'Insertion Sort' },
    { value: 'binary-search', label: 'Binary Search' },
    { value: 'linear-search', label: 'Linear Search' }
  ];

  const speedOptions = [
    { value: 1000, label: 'Slow (1s)' },
    { value: 500, label: 'Medium (0.5s)' },
    { value: 200, label: 'Fast (0.2s)' },
    { value: 50, label: 'Very Fast (0.05s)' }
  ];

  const generateBubbleSortSteps = (arr) => {
    const steps = [];
    const n = arr?.length;
    let tempArr = [...arr];
    
    steps?.push({
      array: [...tempArr],
      comparing: [],
      swapped: [],
      description: 'Starting Bubble Sort - Compare adjacent elements and swap if needed'
    });

    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        steps?.push({
          array: [...tempArr],
          comparing: [j, j + 1],
          swapped: [],
          description: `Comparing elements at positions ${j} and ${j + 1}: ${tempArr?.[j]} and ${tempArr?.[j + 1]}`
        });

        if (tempArr?.[j] > tempArr?.[j + 1]) {
          [tempArr[j], tempArr[j + 1]] = [tempArr?.[j + 1], tempArr?.[j]];
          steps?.push({
            array: [...tempArr],
            comparing: [j, j + 1],
            swapped: [j, j + 1],
            description: `Swapped ${tempArr?.[j + 1]} and ${tempArr?.[j]} because ${tempArr?.[j + 1]} > ${tempArr?.[j]}`
          });
        }
      }
    }

    steps?.push({
      array: [...tempArr],
      comparing: [],
      swapped: [],
      description: 'Bubble Sort completed! Array is now sorted.'
    });

    return steps;
  };

  const generateSelectionSortSteps = (arr) => {
    const steps = [];
    const n = arr?.length;
    let tempArr = [...arr];
    
    steps?.push({
      array: [...tempArr],
      comparing: [],
      swapped: [],
      description: 'Starting Selection Sort - Find minimum element and place it at the beginning'
    });

    for (let i = 0; i < n - 1; i++) {
      let minIdx = i;
      
      steps?.push({
        array: [...tempArr],
        comparing: [i],
        swapped: [],
        description: `Finding minimum element from position ${i} to end`
      });

      for (let j = i + 1; j < n; j++) {
        steps?.push({
          array: [...tempArr],
          comparing: [minIdx, j],
          swapped: [],
          description: `Comparing ${tempArr?.[minIdx]} with ${tempArr?.[j]}`
        });

        if (tempArr?.[j] < tempArr?.[minIdx]) {
          minIdx = j;
          steps?.push({
            array: [...tempArr],
            comparing: [minIdx],
            swapped: [],
            description: `New minimum found: ${tempArr?.[minIdx]} at position ${minIdx}`
          });
        }
      }

      if (minIdx !== i) {
        [tempArr[i], tempArr[minIdx]] = [tempArr?.[minIdx], tempArr?.[i]];
        steps?.push({
          array: [...tempArr],
          comparing: [],
          swapped: [i, minIdx],
          description: `Swapped ${tempArr?.[minIdx]} and ${tempArr?.[i]}`
        });
      }
    }

    steps?.push({
      array: [...tempArr],
      comparing: [],
      swapped: [],
      description: 'Selection Sort completed!'
    });

    return steps;
  };

  const generateBinarySearchSteps = (arr, target = 25) => {
    const steps = [];
    const sortedArr = [...arr]?.sort((a, b) => a - b);
    let left = 0;
    let right = sortedArr?.length - 1;
    
    steps?.push({
      array: sortedArr,
      comparing: [],
      swapped: [],
      description: `Starting Binary Search for target: ${target} (Array must be sorted first)`
    });

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      
      steps?.push({
        array: sortedArr,
        comparing: [left, mid, right],
        swapped: [],
        description: `Checking middle element at position ${mid}: ${sortedArr?.[mid]}`
      });

      if (sortedArr?.[mid] === target) {
        steps?.push({
          array: sortedArr,
          comparing: [mid],
          swapped: [mid],
          description: `Found target ${target} at position ${mid}!`
        });
        break;
      } else if (sortedArr?.[mid] < target) {
        left = mid + 1;
        steps?.push({
          array: sortedArr,
          comparing: [left, right],
          swapped: [],
          description: `${sortedArr?.[mid]} < ${target}, search right half`
        });
      } else {
        right = mid - 1;
        steps?.push({
          array: sortedArr,
          comparing: [left, right],
          swapped: [],
          description: `${sortedArr?.[mid]} > ${target}, search left half`
        });
      }
    }

    if (left > right) {
      steps?.push({
        array: sortedArr,
        comparing: [],
        swapped: [],
        description: `Target ${target} not found in the array`
      });
    }

    return steps;
  };

  const generateSteps = (algorithm, arr) => {
    switch (algorithm) {
      case 'bubble-sort':
        return generateBubbleSortSteps(arr);
      case 'selection-sort':
        return generateSelectionSortSteps(arr);
      case 'binary-search':
        return generateBinarySearchSteps(arr);
      default:
        return [];
    }
  };

  const handleArrayChange = (value) => {
    setInputValue(value);
    try {
      const newArray = value?.split(',')?.map(num => parseInt(num?.trim()))?.filter(num => !isNaN(num));
      if (newArray?.length > 0) {
        setArray(newArray);
      }
    } catch (error) {
      console.error('Invalid array format');
    }
  };

  const runAlgorithm = async () => {
    setIsRunning(true);
    setCurrentStep(0);
    const algorithmSteps = generateSteps(selectedAlgorithm, array);
    setSteps(algorithmSteps);

    for (let i = 0; i < algorithmSteps?.length; i++) {
      setCurrentStep(i);
      await new Promise(resolve => setTimeout(resolve, speed));
      if (!isRunning) break;
    }

    setIsRunning(false);
  };

  const resetVisualization = () => {
    setIsRunning(false);
    setCurrentStep(0);
    setSteps([]);
  };

  const getBarColor = (index, step) => {
    if (!step) return 'bg-accent';
    
    if (step?.swapped && step?.swapped?.includes(index)) {
      return 'bg-green-500';
    } else if (step?.comparing && step?.comparing?.includes(index)) {
      return 'bg-warning';
    } else {
      return 'bg-accent';
    }
  };

  const currentStepData = steps?.[currentStep];
  const displayArray = currentStepData ? currentStepData?.array : array;
  const maxValue = Math.max(...displayArray);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select
          label="Algorithm"
          options={algorithms}
          value={selectedAlgorithm}
          onChange={setSelectedAlgorithm}
        />
        
        <Select
          label="Speed"
          options={speedOptions}
          value={speed}
          onChange={setSpeed}
        />
        
        <Input
          label="Array (comma-separated)"
          value={inputValue}
          onChange={(e) => handleArrayChange(e?.target?.value)}
          placeholder="64,34,25,12,22,11,90"
        />
        
        <div className="flex items-end space-x-2">
          <Button
            variant="default"
            iconName="Play"
            iconPosition="left"
            onClick={runAlgorithm}
            disabled={isRunning}
            className="flex-1"
          >
            {isRunning ? 'Running...' : 'Start'}
          </Button>
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={resetVisualization}
            disabled={isRunning}
          />
        </div>
      </div>
      {/* Visualization */}
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Icon name="BarChart3" size={20} className="text-accent" />
            <span>Algorithm Visualization</span>
          </h3>
          {currentStepData && (
            <div className="text-sm text-text-secondary">
              Step {currentStep + 1} of {steps?.length}
            </div>
          )}
        </div>

        {/* Array Bars */}
        <div className="flex items-end justify-center space-x-2 mb-6 h-64">
          {displayArray?.map((value, index) => (
            <div key={index} className="flex flex-col items-center space-y-2">
              <div
                className={`transition-all duration-300 rounded-t ${getBarColor(index, currentStepData)}`}
                style={{
                  height: `${(value / maxValue) * 200}px`,
                  width: '40px',
                  minHeight: '20px'
                }}
              />
              <div className="text-xs font-mono text-text-secondary">
                {value}
              </div>
              <div className="text-xs text-text-secondary opacity-60">
                {index}
              </div>
            </div>
          ))}
        </div>

        {/* Step Description */}
        {currentStepData && (
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-text-secondary">
              {currentStepData?.description}
            </p>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-accent rounded"></div>
            <span>Default</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-warning rounded"></div>
            <span>Comparing</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>Swapped/Found</span>
          </div>
        </div>
      </div>
      {/* Algorithm Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center space-x-2">
            <Icon name="Info" size={16} className="text-accent" />
            <span>Algorithm Details</span>
          </h4>
          <div className="text-sm text-text-secondary space-y-2">
            {selectedAlgorithm === 'bubble-sort' && (
              <>
                <p><strong>Time Complexity:</strong> O(n²)</p>
                <p><strong>Space Complexity:</strong> O(1)</p>
                <p><strong>Description:</strong> Repeatedly steps through the list, compares adjacent elements and swaps them if they're in the wrong order.</p>
              </>
            )}
            {selectedAlgorithm === 'selection-sort' && (
              <>
                <p><strong>Time Complexity:</strong> O(n²)</p>
                <p><strong>Space Complexity:</strong> O(1)</p>
                <p><strong>Description:</strong> Finds the minimum element and places it at the beginning, then repeats for the remaining elements.</p>
              </>
            )}
            {selectedAlgorithm === 'binary-search' && (
              <>
                <p><strong>Time Complexity:</strong> O(log n)</p>
                <p><strong>Space Complexity:</strong> O(1)</p>
                <p><strong>Description:</strong> Efficiently finds a target value in a sorted array by repeatedly dividing the search interval in half.</p>
              </>
            )}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center space-x-2">
            <Icon name="Lightbulb" size={16} className="text-warning" />
            <span>Learning Tips</span>
          </h4>
          <ul className="text-sm text-text-secondary space-y-1">
            <li>• Watch how elements move during each step</li>
            <li>• Try different array sizes and values</li>
            <li>• Compare time complexities between algorithms</li>
            <li>• Adjust speed to better understand the process</li>
            <li>• Notice the patterns in comparisons and swaps</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AlgorithmVisualizer;